import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'universal-cookie';
import { request } from './helper';
import { AgencyUserRegisterProps, LoginRequest, PasswordRecoveryRequest, SavePasswordRequest } from '@/types';

// Initialize cookies instance
const cookies = new Cookies();

// Helper: Manage cookies
const currentDate = new Date();
const oneYearFromNow = new Date(currentDate.getTime());
oneYearFromNow.setFullYear(currentDate.getFullYear() + 1);

const setToken = (name: string, token: string) => {
  cookies.set(name, token, { path: '/', expires: oneYearFromNow });
};

const removeToken = (name: string) => {
  cookies.remove(name, { path: '/' });
};

export const getToken = (name: string): string => cookies.get(name) || '';

// **createAsyncThunk for Admin Actions**
export const adminLogin = createAsyncThunk<
  any, // Return type (e.g., admin user object)
  LoginRequest, // Request payload
  { rejectValue: string } // Error type
>('auth/adminLogin', async (data, { rejectWithValue }) => {
  const response = await request('admin/login', 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Admin login failed');
});

export const adminLogout = createAsyncThunk<
  void, // No response payload
  void, // No arguments
  { rejectValue: string }
>('auth/adminLogout', async (_, { rejectWithValue }) => {
  const response = await request('admin/logout', 'DELETE');
  return response.success ? undefined : rejectWithValue(response.error || 'Admin logout failed');
});

export const adminMe = createAsyncThunk<
  any, // Return type (e.g., admin profile object)
  void,
  { rejectValue: string }
>('auth/adminMe', async (_, { rejectWithValue }) => {
  const response = await request('admin/me');
  
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to fetch admin details');
});

export const adminLoginAsUser = createAsyncThunk<
  any, // Return type
  number, // User ID
  { rejectValue: string }
>('auth/adminLoginAsUser', async (id, { rejectWithValue }) => {
  const response = await request(`admin/login-as-user/${id}`);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to login as user');
});

// **createAsyncThunk for User Actions**
export const userLogin = createAsyncThunk<
  any,
  LoginRequest,
  { rejectValue: string }
>('auth/userLogin', async (data, { rejectWithValue }) => {
  const response = await request('api/login', 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'User login failed');
});

export const userLogout = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/userLogout',
  async (_, { rejectWithValue }) => {
    const response = await request('api/logout', 'DELETE');
    return response.success ? undefined : rejectWithValue(response.error || 'User logout failed');
  }
);

export const userMe = createAsyncThunk<any, void, { rejectValue: string }>(
  'auth/userMe',
  async (_, { rejectWithValue }) => {
    const response = await request('api/me');
    return response.success ? response.data : rejectWithValue(response.error || 'Failed to fetch user details');
  }
);

export const userRegister = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>('auth/userRegister', async (data, { rejectWithValue }) => {
  const response = await request('api/register', 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Registration failed');
});

export const userPasswordRecovery = createAsyncThunk<
  void,
  PasswordRecoveryRequest,
  { rejectValue: string }
>('auth/userPasswordRecovery', async (data, { rejectWithValue }) => {
  const response = await request('api/recovery', 'POST', data);
  return response.success ? undefined : rejectWithValue(response.error || 'Password recovery failed');
});

export const userSavePassword = createAsyncThunk<
  void,
  SavePasswordRequest,
  { rejectValue: string }
>('auth/userSavePassword', async (data, { rejectWithValue }) => {
  const response = await request('api/save-password', 'POST', data);
  return response.success ? undefined : rejectWithValue(response.error || 'Saving password failed');
});

export const userCheckResetPasswordHash = createAsyncThunk<
  boolean,
  string, // Hash parameter
  { rejectValue: string }
>('auth/userCheckResetPasswordHash', async (hash, { rejectWithValue }) => {
  const response = await request(`api/hash/${hash}`);
  return response.success ? true : rejectWithValue(response.error || 'Invalid reset password hash');
});

export const userUpdateProfile = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>('auth/userUpdateProfile', async (data, { rejectWithValue }) => {
  const response = await request('api/update-profile', 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Profile update failed');
});

export const userCreateAgencyUser = createAsyncThunk<
  any,
  AgencyUserRegisterProps,
  { rejectValue: string }
>('auth/userCreateAgencyUser', async (data, { rejectWithValue }) => {
  const response = await request('api/register/agency-user', 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to create agency user');
});

export const userRegisterAgencyAdmin = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>('auth/userRegisterAgencyAdmin', async (data, { rejectWithValue }) => {
  const response = await request('api/register/agency-admin', 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to register agency admin');
});

export const userLoginAsAgencyUser = createAsyncThunk<
  any,
  number, // User ID
  { rejectValue: string }
>('auth/userLoginAsAgencyUser', async (id, { rejectWithValue }) => {
  const response = await request(`api/login-as-user/${id}`);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to login as agency user');
});

export const userAutoLogin = createAsyncThunk(
  'auth/autoLogin',
  async (data: any, { rejectWithValue }) => {
    const response = await request(`api/autologin`, 'POST', data);
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

// **Auth State Definition**
interface AuthState {
  me: Record<string, any>;
  status: 'idle' | 'loading' | 'success' | 'failed';
  allUsers: any[];
  isLoggedIn: boolean;
  hasNoAccess: boolean;
  isActivated: boolean;
  isValidHash: boolean;
}

// **Initial State**
const initialState: AuthState = {
  me: {},
  status: 'idle',
  allUsers: [],
  isLoggedIn: false,
  hasNoAccess: false,
  isActivated: false,
  isValidHash: false,
};

// **Auth Slice**
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    adminIsLoggedIn: (state) => {
      state.isLoggedIn = !!getToken('admin-gls-token');
    },
    userIsLoggedIn: (state) => {
      state.isLoggedIn = !!getToken('gls-token');
    },
    authHasNoAccess: (state) => {
      state.hasNoAccess = true;
    },
    authIsNotLoggedIn: (state) => {
      state.isLoggedIn = false;
      removeToken('gls-token');
      removeToken('admin-gls-token');
    },
    setMeData: (state, action: PayloadAction<any>) => {
      state.me = action.payload;
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state: AuthState) => {
      state.status = 'loading';
    };
    const setFailed = (state: AuthState) => {
      state.status = 'failed';
    };

    // **Handle Async Actions**
    builder
      .addCase(userRegister.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = true;
        setToken('gls-token', payload.token);
        state.status = 'success';
      })
      
      .addCase(userRegisterAgencyAdmin.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = true;
        setToken('gls-token', payload.token);
        state.status = 'success';
      })

      .addCase(adminLogin.pending, setLoading)
      .addCase(adminLogin.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = true;
        setToken('admin-gls-token', payload.token);
        state.status = 'success';
      })
      .addCase(adminLogin.rejected, setFailed)

      .addCase(userLogin.pending, setLoading)
      .addCase(userLogin.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = true;
        setToken('gls-token', payload.token);
        state.status = 'success';
      })
      .addCase(userLogin.rejected, setFailed)

      .addCase(userAutoLogin.pending, setLoading)
      .addCase(userAutoLogin.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = true;
        setToken('gls-token', payload.token);
        state.status = 'success';
      })
      .addCase(userAutoLogin.rejected, setFailed)

      .addCase(userLogout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.me = {}
        const agencyToken = getToken('gls-agency-token')
        if (agencyToken) {
          setToken('gls-token', agencyToken)
          removeToken('gls-agency-token')
        } else {
          removeToken('gls-token');
        }
      })

      .addCase(adminLogout.fulfilled, (state) => {
        state.isLoggedIn = false;
        removeToken('admin-gls-token');
        state.me = {};
      })

      .addCase(userMe.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = !!payload.id;
      })

      .addCase(adminMe.fulfilled, (state, { payload }) => {
        state.me = payload;
        state.isLoggedIn = true;
      })

      .addCase(adminLoginAsUser.fulfilled, (state, { payload }) => {
        state.isLoggedIn = true
        state.status = 'success'
        state.me = {}
        setToken('gls-token', payload);
      })

      .addCase(userLoginAsAgencyUser.fulfilled, (state, { payload }) => {
        state.isLoggedIn = true
        state.status = 'success'
        const token = getToken('gls-token')
        setToken('gls-agency-token', token);
        state.me = {}
        setToken('gls-token', payload);
      })
      
      .addCase(userCheckResetPasswordHash.pending, setLoading)
      .addCase(userCheckResetPasswordHash.fulfilled, (state, { payload }) => {
        state.isValidHash = payload;
        state.status = 'success';
      })
      .addCase(userCheckResetPasswordHash.rejected, setFailed);
  },
});

// **Export Actions**
export const { adminIsLoggedIn, userIsLoggedIn, authHasNoAccess, authIsNotLoggedIn, setMeData } = authSlice.actions;

// **Export Reducer**
export default authSlice.reducer;